import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";

const COURSE_BG =
  "/brand/proshop-floor.jpg";

const SERIES = [
  { key: "top_items", title: "Most Requested Items", subtitle: "What members want sourced most often." },
  { key: "top_categories", title: "Top Order Categories", subtitle: "By submission volume." },
  { key: "top_travel_origins", title: "Travel-Origin Trends", subtitle: "Where members spotted the items they're requesting." },
  { key: "top_wishlist", title: "Top Wishlist (Shared)", subtitle: "Most-wished items members have shared with the club." },
  { key: "top_colors", title: "Top Color Trends", subtitle: "Across active orders." },
  { key: "top_member_brands", title: "Member Favorite Brands", subtitle: "From Crest Profile preferences." },
  { key: "top_member_categories", title: "Member Favorite Categories", subtitle: "From Crest Profile preferences." },
  { key: "top_styles", title: "Member Style Preferences", subtitle: "From Crest Profile preferences." },
];

export default function AdminIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/intelligence")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const download = (path, filename) => {
    // Trigger a real browser download via a hidden link; cookies are sent automatically.
    const base = api.defaults.baseURL || "";
    const a = document.createElement("a");
    a.href = `${base}${path}`;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="bg-cream min-h-screen" data-testid={ADMIN.intelligencePage}>
      <section className="relative h-64 overflow-hidden border-b border-[#E5E3D8]">
        <img src={COURSE_BG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1A422D]/70" />
        <div className="absolute inset-0 flex items-end max-w-7xl mx-auto px-6 sm:px-10 py-8">
          <div className="text-[#F9F8F4]">
            <div className="overline" style={{ color: "#D9C68A" }}>Pro Shop · Analytics</div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 leading-none">Demand Intelligence</h1>
            <p className="mt-3 max-w-2xl text-sm text-[#F9F8F4]/80">
              What members are asking for, what they prefer, and how demand is moving — at a glance.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-12 space-y-10">
        {loading && <div className="overline text-[#7a7a7a]">Loading…</div>}
        {error && (
          <div className="px-4 py-3 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">
            {error}
          </div>
        )}

        {data && (
          <>
            {/* Totals */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Total Orders" value={data.totals.orders} testid={ADMIN.intelTotalOrders} />
              <Kpi label="Total Members" value={data.totals.members} testid={ADMIN.intelTotalMembers} />
              <Kpi
                label="Onboarded"
                value={`${data.totals.onboarded_members} / ${data.totals.members}`}
                testid={ADMIN.intelOnboarded}
                hint={`${pct(data.totals.onboarded_members, data.totals.members)}% of members`}
              />
              <Kpi
                label="30-Day Growth"
                value={data.totals.growth_pct_30d === null ? "—" : `${data.totals.growth_pct_30d}%`}
                testid={ADMIN.intelGrowth}
                hint={`${data.totals.orders_30d} vs ${data.totals.orders_prior_30d} prior 30d`}
              />
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Kpi label="Active Members (30d)" value={data.totals.active_members_30d} />
              <Kpi label="Active Members (90d)" value={data.totals.active_members_90d} />
              <Kpi label="Wishlist Items" value={data.totals.wishlist_items ?? 0} hint={`${data.totals.wishlist_shared ?? 0} shared with club`} />
              <Kpi label="Orders (last 30d)" value={data.totals.orders_30d} hint={`vs ${data.totals.orders_prior_30d} prior 30d`} />
            </div>

            {/* Exports */}
            <div className="surface-card p-8">
              <div className="overline">Exports</div>
              <h2 className="font-serif text-2xl mt-1 text-[#1A1A1A]">Download Reports</h2>
              <p className="text-sm text-[#4A4A4A] mt-2">
                CSV downloads suitable for Excel, Google Sheets, or any spreadsheet tool.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => download("/admin/exports/orders.csv", "orders.csv")}
                  data-testid={ADMIN.intelExportOrders}
                  className="btn-secondary text-xs"
                >
                  Orders.csv
                </button>
                <button
                  type="button"
                  onClick={() => download("/admin/exports/members.csv", "members.csv")}
                  data-testid={ADMIN.intelExportMembers}
                  className="btn-secondary text-xs"
                >
                  Members.csv
                </button>
                <button
                  type="button"
                  onClick={() => download("/admin/exports/intelligence.csv", "intelligence.csv")}
                  data-testid={ADMIN.intelExportIntel}
                  className="btn-primary"
                >
                  Intelligence Snapshot.csv
                </button>
              </div>
            </div>

            {/* Velocity */}
            <VelocityCard rows={data.submission_velocity || []} />

            {/* Series cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {SERIES.map((s) => (
                <SeriesCard
                  key={s.key}
                  title={s.title}
                  subtitle={s.subtitle}
                  rows={data[s.key] || []}
                  testid={`intel-${s.key.replace(/_/g, "-")}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function pct(num, denom) {
  if (!denom) return 0;
  return Math.round((num / denom) * 100);
}

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="surface-card p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-serif text-3xl mt-2 text-[#1A1A1A]">{value}</div>
      {hint && <div className="text-xs text-[#7A7A7A] mt-1">{hint}</div>}
    </div>
  );
}

function SeriesCard({ title, subtitle, rows, testid }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
  return (
    <div className="surface-card p-6" data-testid={testid}>
      <div className="overline">Top {rows.length || 0}</div>
      <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">{title}</h3>
      {subtitle && <p className="text-xs text-[#7A7A7A] mt-1">{subtitle}</p>}
      <div className="mt-5 space-y-2.5">
        {rows.length === 0 ? (
          <p className="text-sm text-[#9A9A9A]">No data yet.</p>
        ) : (
          rows.map((r) => (
            <div key={r.label} className="flex items-center gap-3">
              <div className="w-40 sm:w-56 truncate text-sm text-[#1A1A1A]">{r.label}</div>
              <div className="flex-1 bg-[#F3F1E9] h-3 relative overflow-hidden">
                <div
                  className="bg-[#1A422D] h-full"
                  style={{ width: `${Math.max(4, (r.count / max) * 100)}%` }}
                />
              </div>
              <div className="w-10 text-right text-sm tabular-nums text-[#4A4A4A]">{r.count}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function VelocityCard({ rows }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count || 0), 0) || 1;
  return (
    <div className="surface-card p-6">
      <div className="overline">Last 12 Weeks</div>
      <h3 className="font-serif text-xl mt-1 text-[#1A1A1A]">Submission Velocity</h3>
      <p className="text-xs text-[#7A7A7A] mt-1">Orders placed per ISO week.</p>
      <div className="mt-6 flex items-end gap-1.5 h-32">
        {rows.length === 0 ? (
          <p className="text-sm text-[#9A9A9A]">No data yet.</p>
        ) : (
          rows.map((r) => (
            <div key={`${r.year}-${r.week}`} className="flex-1 flex flex-col items-center gap-1.5">
              <div className="w-full flex-1 flex items-end">
                <div
                  className="w-full bg-[#1A422D]"
                  style={{ height: `${Math.max(4, (r.count / max) * 100)}%` }}
                  title={`Week ${r.week}, ${r.year}: ${r.count}`}
                />
              </div>
              <div className="text-[10px] text-[#7A7A7A]">W{String(r.week).padStart(2, "0")}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
